import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	OnInit,
	ViewChild,
} from "@angular/core";
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatTabGroup } from "@angular/material/tabs";
import { CONSTANT } from "src/app/constant";
import { OBComments } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { SharedService } from "src/app/shared/services/shared.service";
import { ContainerRatingsService } from "../../services/base/container-ratings.services";

// at top
type ManeuverOpt = { code: string; description?: string; subDescription?: string };

@Component({
	selector: "app-candidate-rating-fixed-maneuver",
	templateUrl: "./candidate-rating-fixed-maneuver.component.html",
	styleUrls: ["./candidate-rating-fixed-maneuver.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CandidateRatingFixedManeuverComponent implements OnInit, AfterViewInit {
	@ViewChild("baseContainer") baseContainer?: ElementRef;
	rfForm!: UntypedFormGroup;
	formType!: string;
	parentForm!: UntypedFormArray;
	parentFormArrayIndex: number = 0;
	panelOpenState: boolean = false;
	ratingAVal: number = 0;
	ratingBVal: number = 0;
	ratingCVal: number = 0;
	ratingFinalVal: number = 0;
	tabIndex: number = 0;
	isCabinCrewForm = false;
	CANDIDATE_ROLE = CONSTANT.CANDIDATE_ROLE;
	readonly CONSTANT = CONSTANT;

	@Input("rfForm") set _(value: any) {
		this.rfForm = value as UntypedFormGroup;
	}
	@ViewChild("maneuverTabs") maneuverTabs!: MatTabGroup;
	@ViewChild("customTabContainer") customTabContainer!: ElementRef;
	obComments: Array<OBComments> = [];
	@ViewChild(MatTabGroup) tabGroup!: MatTabGroup;

	@Input() loaded: boolean = false;
	@Input() showTem: boolean = false;
	loadexpandManeuver: boolean = false;

	constructor(
		public sharedService: SharedService,
		private cd: ChangeDetectorRef,
		private elRef: ElementRef,
		private dialog: MatDialog,
		private containerRatingsService: ContainerRatingsService,
	) {
		const isManeuverExpanded = localStorage.getItem(CONSTANT.STORAGE_KEY.EXPANDED_MANEUVER);

		this.loadexpandManeuver = isManeuverExpanded && JSON.parse(isManeuverExpanded);
	}

	ngOnInit(): void {
		this.parentForm = this.rfForm.parent as UntypedFormArray;
		this.parentFormArrayIndex = this.parentForm.controls.findIndex((subform) => subform == this.rfForm);

		this.buildManeuverDescMap();

		this.containerRatingsService.getClearComments().subscribe((val) => {
			if (val) {
				this.obComments = [];
			}
		});
	}

	ngAfterViewInit(): void {
		const tabLabels = this.elRef.nativeElement.querySelectorAll(".mat-mdc-tab-list");

		for (const t of tabLabels) {
			t.remove();
		}
	}

	// inside the class
	maneuverDescMap: Record<string, string> = {};

	private buildManeuverDescMap(): void {
		const formType = this.containerRatingsService.formType;
		this.isCabinCrewForm = formType == CONSTANT.FORM_TYPE.cabin_crew;
		const maneuvers: ManeuverOpt[] = this.sharedService.formMasterData?.[formType!]?.maneuvers ?? [];

		this.maneuverDescMap = maneuvers.reduce<Record<string, string>>((acc, m) => {
			acc[m.code] = m.subDescription ?? m.description ?? "";
			return acc;
		}, {});
	}

	get baseContainerWidth() {
		return this.baseContainer?.nativeElement.offsetWidth ?? 0;
	}

	get selectedIndex(): number {
		return this.tabGroup.selectedIndex ?? 0;
	}

	get traineeAssessmentId() {
		return this.rfForm?.get("traineeAssessmentId")!;
	}

	get candidateName() {
		return this.rfForm.get("candidateName")!;
	}

	get observations() {
		return this.rfForm.controls["observations"] as UntypedFormArray;
	}

	otherComments(index: number) {
		return this.observations.at(index).get("otherComents")!;
	}

	get fixedManeuvers() {
		return this.observations.value.map((v: any) => {
			// console.log("all", v);
			return v.maneuver;
		});
	}

	get maneuvers() {
		// console.log("maneuver observation", this.observations.value);
		return this.observations.value.map((v: any) => {
			return v.maneuver;
		});
	}

	get TEMValue() {
		return this.observations.value.map((v: any) => {
			return v.safetyOutcome;
		});
	}

	getSubdescription(code: any) {
		if (this.containerRatingsService.formType) {
			const maneuver = this.sharedService.formMasterData?.[this.containerRatingsService.formType].maneuvers;
			const description = maneuver.find((man: any) => {
				return man.code == code;
			});
			return description.subDescription;
		}

		return code;
	}

	listallOBComments(val: OBComments) {
		if (!this.obComments.includes(val)) {
			val.maneuver != CONSTANT.DUMMY_PLACEHOLDER ? val.maneuver : "";
			const lastStateHistory = this.obComments.filter((comment: any) => {
				if (comment.observationCode == val.observationCode) return comment;
			});

			let isLastStateDisabled = false;

			if (lastStateHistory.length > 0) {
				if (lastStateHistory[lastStateHistory.length - 1]?.isDisabled) {
					isLastStateDisabled = true;
				}
			}

			this.obComments.push({ ...val, lastActionDisabled: isLastStateDisabled });
		}
	}

	setTabIndex(index: number) {
		this.maneuverTabs.selectedIndex = index;
		this.tabIndex = index;
	}

	viewSummaryModal() {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "view-rating-summary-modal",
			height: "560px",
			width: "1154px",
			hasBackdrop: true,
			data: {
				modalTitle: "Summary",
				modalName: "viewRatingSummaryModal",
				modalContent: this.obComments,
				modalTraineeAssessmentId: this.traineeAssessmentId.value,
				noCloseBtn: true,
				modalBtnConfirmText: "Close",
			},
		});

		const sub = dialogRef.afterClosed().subscribe((a) => {
			sub.unsubscribe();
		});
	}

	selectedTabValue(event: any) {
		this.tabIndex = event.index;

		this.cd.detectChanges();
	}

	showExpension(maneuver: AbstractControl | null): boolean {
		if (!maneuver) return false;

		const v = (maneuver as any)?.value;
		if (!v || typeof v !== "object") return false;

		const baseFields = new Set([
			"assessmentId",
			"candidateId",
			"maneuver",
			"maneuverCode",
			"otherComents",
			"repeat",
			"safetyOutcome",
			"selectedOBs",
			"traineeAssessmentId",
		]);

		// remove base keys; check remaining count
		const remainingKeys = Object.keys(v).filter((k) => !baseFields.has(k));
		return remainingKeys.length > 1; // "more than one" → true, else false
	}

	onManeuverExpand() {
		const container = this.customTabContainer.nativeElement;
		container.scrollLeft = 0;

		this.loadexpandManeuver = !this.loadexpandManeuver;
		localStorage.setItem(CONSTANT.STORAGE_KEY.EXPANDED_MANEUVER, JSON.stringify(this.loadexpandManeuver));
	}

	/**
	 * Check if TEM is shown and not selected for a given maneuver observation
	 * Returns true if TEM is visible (showTem is true), not disabled, and safetyOutcome is not selected (value is -2, 0, or undefined)
	 */
	isTEMIncomplete(observationForm: AbstractControl | null): boolean {
		// If TEM is not shown, don't show warning
		if (!this.showTem) return false;

		if (!observationForm) return false;

		const maneuverForm = observationForm as UntypedFormGroup;
		if (!maneuverForm) return false;

		const safetyOutcomeControl = maneuverForm.get("safetyOutcome");
		if (!safetyOutcomeControl) return false;

		// If TEM control is disabled, don't show warning
		if (safetyOutcomeControl.disabled) return false;

		const safetyOutcomeValue = safetyOutcomeControl.value;

		// TEM is incomplete if value is -2 (not set), 0, or undefined/null
		// Valid values are 1-5 (ratings) or -1 (N/A)
		return (
			safetyOutcomeValue === -2 ||
			safetyOutcomeValue === 0 ||
			safetyOutcomeValue === null ||
			safetyOutcomeValue === undefined
		);
	}
}
